import React, { ReactElement, useState, useCallback, useMemo } from 'react';
import { Flex, Text } from 'rebass/styled-components';
import { useParams } from 'react-router-dom';

import AddEditCustomRecords from '../AddEditCustomRecords';
import useCustomRecordsColumns from './useCustomRecordsColumns';

import { Table, SearchInput } from 'ui/patterns';
import { Button, Stack, Separator } from 'ui/components';

import { useGetCustomRecords } from 'hooks';

import * as Icon from 'assets/icons';

import { CustomRecordTableProps } from './customRecordsTypes';
import { CustomRecord } from 'types/customTables';

export default function CustomRecords({
  selectedTable,
  toggleRecordsGrid,
}: CustomRecordTableProps): ReactElement {
  const sortItem = { sortKey: selectedTable?.Columns[0], sortOrder: 'asc' };
  const [showPanel, setShowPanel] = useState(false);
  const [record, setRecord] = useState(undefined);
  const [columnsSearch, setColumnsSearch] = useState({}); // table header search
  const [currentSortItem, setCurrentSortItem] = useState(sortItem);
  const [keywordSearch, setKeywordSearch] = useState(''); //keyword search
  const [mode, setMode] = useState(''); //add or edit

  const { accountId } = useParams<{
    accountId: string;
    tabId?: string;
  }>();

  const tableId = selectedTable?.Id;
  const columns = useCustomRecordsColumns(selectedTable?.Columns);

  const toggleAddPanel = () => {
    setShowPanel(false);
    setRecord(undefined);
  };

  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetCustomRecords(
    columnsSearch,
    currentSortItem,
    keywordSearch,
    parseInt(accountId),
    tableId,
    selectedTable?.Columns,
  );

  const headerUpdateCallBack = useCallback(
    ({ columnId, value }: { columnId: string; value: string }) => {
      setColumnsSearch({
        ...columnsSearch,
        [columnId]: value,
      });
    },
    [columnsSearch],
  );

  const handleSortChange = useCallback(
    (item: string): void => {
      let sortOrder = 'asc';
      if (
        currentSortItem.sortKey === item &&
        currentSortItem.sortOrder === 'asc'
      ) {
        sortOrder = 'desc';
      }
      setCurrentSortItem({
        sortKey: item,
        sortOrder,
      });
    },
    [setCurrentSortItem, currentSortItem],
  );

  const tableData = useMemo(() => {
    if (data) {
      return data?.pages
        ?.map((dataItem: any) => dataItem.Data)
        .flat();
    }
  }, [data]);

  const totalItems = useMemo(() => {
    if (data && data.pages[0]) {
      return data.pages[0].FilteredRecords;
    }
    return 0;
  }, [data]);

  const handleKeywordSearch = (value: string): void => {
    setKeywordSearch(value);
  };

  const handleReset = (): void => {
    setKeywordSearch('');
    setColumnsSearch({});
    setCurrentSortItem(sortItem);
  };

  const handleEditRecord = (row: any): void => {
    setRecord(row);
    setShowPanel(true);
    setMode('Edit');
  };

  const onEndReached = () => {
    if (tableData) {
      if (hasNextPage && tableData?.length < totalItems) fetchNextPage();
    }
  };
  return (
    <>
      <Flex justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="x" gap={2} style={{ alignItems: 'center' }}>
          <Button
            variant="secondary"
            iconBefore={<Icon.ChevronLeft />}
            onClick={toggleRecordsGrid}
          >
            Back
          </Button>
          <Text
            fontSize={1}
            color="text.body"
            fontWeight="bold"
            sx={{ height: '20px' }}
          >
            {selectedTable?.TableName}
          </Text>
          <Separator direction="y" style={{ minHeight: '20px' }} />
          <Text fontSize={1} color="text.body" sx={{ height: '20px' }}>
            {tableData &&
              tableData.length > 0 &&
              `Showing ${tableData?.length || 0}
               of ${totalItems}`}
            {isFetching && '  Processing'}
          </Text>
        </Stack>
        <Stack direction="x" gap={2}>
          <SearchInput
            placeholder="Search Records"
            handleChange={handleKeywordSearch}
            value={keywordSearch}
          />
          <Button
            iconBefore={<Icon.Refresh title="Refresh" />}
            variant="secondary"
            onClick={handleReset}
          >
            Reset All
          </Button>
          <Button
            iconBefore={<Icon.Add title="Add" />}
            variant="primary"
            onClick={() => {
              setRecord(undefined);
              setShowPanel(true);
              setMode('Add');
            }}
          >
            Add
          </Button>
        </Stack>
      </Flex>

      <Table
        columns={columns}
        data={tableData ? tableData : undefined}
        isLoading={isLoading}
        headerUpdateCallBack={headerUpdateCallBack}
        handleSortChange={handleSortChange}
        sortedItem={currentSortItem}
        columnsSearch={columnsSearch}
        //onRowDoubleClick={handleEditRecord}
        onRecordEditPress={handleEditRecord}
        masterTableId={selectedTable?.Id}
        onEndReached={onEndReached}
        isFetching={!!isFetchingNextPage}
      />

      <AddEditCustomRecords
        showPanel={showPanel}
        togglePanel={toggleAddPanel}
        record={record}
        selectedTable={selectedTable}
        accountId={parseInt(accountId)}
        tableId={tableId}
        mode={mode}
      />
    </>
  );
}
